import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import cls from 'classnames';

import {
  analytics,
  getLeadGenSubmitDetail,
  getLeadGenSubmitSuccess,
} from '@/utils/analytics';
import { isDDEnv, isWHEnv } from '@/utils/helper';

import TextInput from '@/atoms/TextInput/TextInput';
import Button from '@/atoms/Button/Button';
import Text from '@/atoms/Text/Text';
import Checkbox from '@/atoms/Checkbox/Checkbox';
import Select from '@/atoms/Select/Select';
import RichText from '@/molecules/RichText/RichText';

import { CtfLeadGenType } from '@/utils/constants';

function LeadGeneration({ fields, entries }) {
  const {
    title,
    disclaimer,
    subscribe,
    backgroundImage,
    fullWidth,
    id,
    type,
    ctaLabel,
    thankYouText,
    marketing,
  } = fields;
  const [name, setName] = useState('');
  const [nameIsFocus, setNameIsFocus] = useState(false);
  const [email, setEmail] = useState('');
  const [emailIsFocus, setEmailIsFocus] = useState(false);
  const [selectedMarketing, setSelectedMarketing] = useState(null);
  const [marketingIsFocus, setMarketingIsFocus] = useState(false);
  const [leadGenEnquiryIsSent, setLeadGenEnquiryIsSent] = useState(false);

  const { locale } = useRouter();

  const isKR = locale === 'ko-KR';
  const marketings = useRef([
    { label: '-', value: null },
    { label: isKR ? '예' : 'Yes', value: 'yes' },
    { label: isKR ? '아니요' : 'No', value: 'no' },
  ]);
  //load recaptcha script if not already exist
  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`
    );
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();

    enquiryAnalytics(e);

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then((token) => {
          submitLeadGenEnquiry(token);
        });
    });
  };

  useEffect(() => {
    if (leadGenEnquiryIsSent) {
      enquirySuccessAnalytics();
    }
  }, [leadGenEnquiryIsSent]);

  const enquiryAnalytics = (e) => {
    analytics({
      window,
      document,
      detail: getLeadGenSubmitDetail(e, {
        isPromotional: type === CtfLeadGenType.OFFER,
      }),
    });
  };

  const enquirySuccessAnalytics = () => {
    analytics({
      window,
      document,
      detail: getLeadGenSubmitSuccess(null, {
        isPromotional: type === CtfLeadGenType.OFFER,
      }),
    });
  };

  const submitLeadGenEnquiry = async (token) => {
    const params = {
      name,
      email,
      marketing: marketings.current.find((i) => i.value === selectedMarketing)
        ?.value || null,
      locale,
      'g-recaptcha-response': token,
    };

    const response = await fetch(
      `/api/lead?${new URLSearchParams(params).toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        //body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    } else {
      setLeadGenEnquiryIsSent(true);
    }
  };

  const isDD = isDDEnv();
  const isWH = isWHEnv();

  return (
    <div
      className={`aaco-lead-generation ${
        !fullWidth ? 'container my-[80px] mx-auto' : ''
      }`}
      id={id}
    >
      <div
        className={cls(
          `w-full bg-cover px-[30px] md:px-0 py-[81px] md:py-[80px] flex justify-center items-center ${
            !fullWidth ? 'rounded-[8px]' : ''
          } `,
          { 'bg-warmer-grey': isWH }
        )}
        style={{
          backgroundImage:
            backgroundImage?.[0]?.original_secure_url &&
            `url(${backgroundImage?.[0]?.original_secure_url})`,
        }}
      >
        <div
          className={cls(
            'w-full',
            {
              'md:w-[840px]': isDD,
              'md:w-[820px]': isWH,
            },
            'flex flex-col justify-center items-center'
          )}
        >
          {title && title.length > 0 && (
            <Text
              tag={'h5'}
              style={cls(
                'font-heading font-h4-mobile md:font-h5 text-h4-mobile md:text-h5 mb-[40px] text-center',
                {
                  'text-white': isDD,
                  'text-acacia': isWH,
                }
              )}
            >
              {title}
            </Text>
          )}
          <form
            className="w-full flex flex-col justify-center items-center"
            onSubmit={handleFormSubmit}
          >
            <div
              className={cls(
                'w-full',
                {
                  'md:w-[596.5px]': isDD,
                  'md:w-[602px]': isWH,
                },
                'flex flex-col md:flex-row justify-between',
                {
                  'gap-x-[26.5px]': isDD,
                  'gap-x-8': isWH,
                },
                'mb-[30px]'
              )}
            >
              <div className="flex-1 mb-[30px] md:mb-0">
                <TextInput
                  value={name}
                  name="lead-gen-name"
                  onChange={(e) => setName(e.target.value)}
                  onFocus={() => setNameIsFocus(true)}
                  onBlur={() => setNameIsFocus(false)}
                  placeholder={locale === 'ko-KR' ? '이름' : 'Name'}
                  required={true}
                  isFocus={nameIsFocus}
                />
              </div>
              <div className="flex-1">
                <TextInput
                  name="lead-gen-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailIsFocus(true)}
                  onBlur={() => setEmailIsFocus(false)}
                  placeholder={locale === 'ko-KR' ? '이메일' : 'Email'}
                  required={true}
                  isFocus={emailIsFocus}
                  type="email"
                />
              </div>
            </div>
            {marketing && (
              <div className="w-full md:w-[600px] grid grid-cols-1 justify-between gap-x-[26.5px] mb-[30px]">
                <div className="flex-1 mb-[30px] md:mb-0">
                  <Select
                    name="marketing"
                    placeholder={marketing}
                    value={selectedMarketing === '-' ? null : selectedMarketing}
                    options={marketings.current}
                    onChange={(e) => setSelectedMarketing(e.target.value)}
                    onFocus={() => setMarketingIsFocus(true)}
                    onBlur={() => setMarketingIsFocus(false)}
                    isFocus={marketingIsFocus}
                    tabIndex={7}
                  />
                </div>
                <div></div>
              </div>
            )}
            <label className="text-small font-small font-text text-white flex items-center gap-x-2 mb-[15px]">
              <Checkbox required={true} className={'translate-y-[-1.5px]'} />
              {disclaimer && !disclaimer.content && (
                <p
                  className={cls('block md:w-[600px] mx-auto', {
                    'font-text font-body text-body text-white': isDD,
                    'font-tag text-tag text-acacia uppercase': isWH,
                  })}
                >
                  {disclaimer}
                </p>
              )}
              {disclaimer &&
                disclaimer.content &&
                disclaimer.content.length > 0 &&
                disclaimer.content.map(({ nodeType, content }, index) => (
                  <RichText
                    entries={entries}
                    nodeType={nodeType}
                    content={content}
                    key={index}
                    richTextStyle={{
                      paragraph: {
                        customFont: cls({
                          'text-white': isDD,
                          'font-tag text-tag text-acacia uppercase': isWH,
                        }),
                      },
                    }}
                  />
                ))}
            </label>
            <label
              className={cls(
                'flex items-center gap-x-2 mb-[35.5px] md:mb-[40.21px]',
                'text-small font-small font-text',
                {
                  'text-white': isDD,
                  'text-acacia': isWH,
                }
              )}
            >
              {subscribe && (
                <Checkbox required={true} className={'translate-y-[-1.5px]'} />
              )}
              {subscribe && !subscribe.content && (
                <p
                  className={cls(
                    'block md:w-[600px] mx-auto',
                    'font-text font-body text-body',
                    { 'text-white': isDD, 'text-acacia': isWH }
                  )}
                >
                  {subscribe}
                </p>
              )}
              {subscribe &&
                subscribe.content &&
                subscribe.content.length > 0 &&
                subscribe.content.map(({ nodeType, content }, index) => (
                  <RichText
                    entries={entries}
                    nodeType={nodeType}
                    content={content}
                    key={index}
                    richTextStyle={{
                      paragraph: {
                        customFont: isDD ? 'text-white' : 'text-acacia',
                      },
                    }}
                  />
                ))}
            </label>
            {!leadGenEnquiryIsSent ? (
              <Button
                type={'submit'}
                style={
                  'aaco-cta link-invert cursor-pointer flex items-center bg-eucalyptus rounded-button w-fit px-5 h-10 dd-kr:bg-actual-dd-gold dd-en:bg-actual-dd-gold'
                }
                tabIndex={8}
              >
                <Text tag="button" style="text-uppercase text-white">
                  {ctaLabel && ctaLabel.length > 0 ? ctaLabel : '회원가입'}
                </Text>
              </Button>
            ) : (
              <Text
                tag="button"
                style={cls({ 'text-white': isDD, 'text-acacia': isWH })}
              >
                {thankYouText && thankYouText.length > 0
                  ? thankYouText
                  : 'Thank you'}
              </Text>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

LeadGeneration.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

LeadGeneration.defaultProps = {
  assets: [],
  entries: [],
  fields: {},
};

export default LeadGeneration;
